export const WALLPAPER_OPTIONS = [
  { id: 'none', name: 'None', description: 'Default theme background' },
  { id: 'hearts', name: 'Hearts', description: 'Floating love hearts', emoji: '\u2665' },
  { id: 'stars', name: 'Stars', description: 'Sparkly star field', emoji: '\u2605' },
  { id: 'music', name: 'Music', description: 'Musical notes', emoji: '\u266B' },
  { id: 'bubbles', name: 'Bubbles', description: 'Soft floating bubbles', emoji: '\u25CF' },
  { id: 'diamonds', name: 'Diamonds', description: 'Luxury diamond pattern', emoji: '\u25C6' },
  { id: 'clouds', name: 'Clouds', description: 'Dreamy soft clouds', emoji: '\u2601' },
  { id: 'sparkle', name: 'Sparkle', description: 'Twinkling sparkles', emoji: '\u2728' },
  { id: 'flowers', name: 'Flowers', description: 'Spring florals', emoji: '\u273F' },
  { id: 'waves', name: 'Waves', description: 'Gentle wave lines', emoji: '\u223C' },
  { id: 'confetti', name: 'Confetti', description: 'Party confetti', emoji: '\u25A0' },
];

// Generate floating elements as inline CSS for a wallpaper
export const getWallpaperCSS = (wallpaperId, accentColor) => {
  if (!wallpaperId || wallpaperId === 'none') return '';
  const wp = WALLPAPER_OPTIONS.find(w => w.id === wallpaperId);
  if (!wp) return '';

  const c = accentColor || '#e91e90';
  const c2 = accentColor ? adjustColor(accentColor, 30) : '#ff69b4';
  const c3 = accentColor ? adjustColor(accentColor, -30) : '#c71585';

  const items = generateItems(wallpaperId, c, c2, c3);

  return `
.wallpaper-container { position: relative; overflow: hidden; }
.wallpaper-container::before {
  content: '';
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  pointer-events: none;
  z-index: 0;
  ${items.bgCSS}
}
.wallpaper-content { position: relative; z-index: 1; }
${items.extraCSS}
`;
};

function adjustColor(hex, amount) {
  hex = hex.replace('#', '');
  const r = Math.min(255, Math.max(0, parseInt(hex.substring(0, 2), 16) + amount));
  const g = Math.min(255, Math.max(0, parseInt(hex.substring(2, 4), 16) + amount));
  const b = Math.min(255, Math.max(0, parseInt(hex.substring(4, 6), 16) + amount));
  return `#${r.toString(16).padStart(2,'0')}${g.toString(16).padStart(2,'0')}${b.toString(16).padStart(2,'0')}`;
}

function generateItems(id, c, c2, c3) {
  switch (id) {
    case 'hearts':
      return {
        bgCSS: `
          background-image:
            radial-gradient(${c}18 1px, transparent 1px),
            radial-gradient(${c2}12 1px, transparent 1px);
          background-size: 60px 60px, 40px 40px;
          background-position: 0 0, 20px 20px;
        `,
        extraCSS: genFloatingElements('\u2665', c, c2, c3, 18)
      };
    case 'stars':
      return {
        bgCSS: `
          background-image:
            radial-gradient(${c}15 1.5px, transparent 1.5px),
            radial-gradient(${c2}10 1px, transparent 1px),
            radial-gradient(${c3}08 0.5px, transparent 0.5px);
          background-size: 80px 80px, 50px 50px, 30px 30px;
          background-position: 0 0, 25px 25px, 10px 40px;
        `,
        extraCSS: genFloatingElements('\u2605', c, c2, c3, 15)
      };
    case 'music':
      return {
        bgCSS: `
          background-image: radial-gradient(${c}12 1px, transparent 1px);
          background-size: 50px 50px;
        `,
        extraCSS: genFloatingElements('\u266B', c, c2, c3, 12)
      };
    case 'bubbles':
      return {
        bgCSS: `
          background-image:
            radial-gradient(circle at 20% 30%, ${c}08 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, ${c2}06 0%, transparent 50%),
            radial-gradient(circle at 50% 50%, ${c3}04 0%, transparent 50%);
          background-size: 100% 100%;
        `,
        extraCSS: genFloatingElements('\u25CB', c, c2, c3, 20)
      };
    case 'diamonds':
      return {
        bgCSS: `
          background-image:
            linear-gradient(45deg, ${c}06 25%, transparent 25%),
            linear-gradient(-45deg, ${c}06 25%, transparent 25%),
            linear-gradient(45deg, transparent 75%, ${c}06 75%),
            linear-gradient(-45deg, transparent 75%, ${c}06 75%);
          background-size: 40px 40px;
          background-position: 0 0, 0 20px, 20px -20px, -20px 0;
        `,
        extraCSS: genFloatingElements('\u25C6', c, c2, c3, 10)
      };
    case 'clouds':
      return {
        bgCSS: `
          background-image:
            radial-gradient(ellipse at 15% 20%, ${c}0a 0%, transparent 60%),
            radial-gradient(ellipse at 75% 40%, ${c2}08 0%, transparent 55%),
            radial-gradient(ellipse at 40% 80%, ${c3}06 0%, transparent 50%),
            radial-gradient(ellipse at 90% 15%, ${c}05 0%, transparent 45%);
          background-size: 100% 100%;
        `,
        extraCSS: genFloatingElements('\u2601', c, c2, c3, 8)
      };
    case 'sparkle':
      return {
        bgCSS: `
          background-image:
            radial-gradient(${c}20 1px, transparent 1px),
            radial-gradient(${c2}15 1.5px, transparent 1.5px);
          background-size: 70px 70px, 100px 100px;
          background-position: 0 0, 35px 35px;
        `,
        extraCSS: genFloatingElements('\u2726', c, c2, c3, 20)
      };
    case 'flowers':
      return {
        bgCSS: `
          background-image: radial-gradient(${c}10 2px, transparent 2px);
          background-size: 45px 45px;
        `,
        extraCSS: genFloatingElements('\u2740', c, c2, c3, 14)
      };
    case 'waves':
      return {
        bgCSS: `
          background-image:
            repeating-linear-gradient(0deg, transparent, transparent 40px, ${c}08 40px, ${c}08 41px),
            repeating-linear-gradient(90deg, transparent, transparent 40px, ${c}05 40px, ${c}05 41px);
          background-size: 41px 41px;
        `,
        extraCSS: genFloatingElements('\u223C', c, c2, c3, 10)
      };
    case 'confetti':
      return {
        bgCSS: `
          background-image:
            radial-gradient(${c}18 2px, transparent 2px),
            radial-gradient(${c2}15 1.5px, transparent 1.5px),
            radial-gradient(${c3}12 2.5px, transparent 2.5px);
          background-size: 53px 53px, 37px 37px, 67px 67px;
          background-position: 0 0, 15px 30px, 40px 10px;
        `,
        extraCSS: genFloatingElements('\u25A0', c, c2, c3, 25)
      };
    default:
      return { bgCSS: '', extraCSS: '' };
  }
}

function genFloatingElements(char, c, c2, c3, count) {
  const colors = [c, c2, c3];
  let css = '';
  for (let i = 0; i < count; i++) {
    const left = (i * 37 + 5) % 100;
    const delay = (i * 1.3) % 8;
    const dur = 12 + (i % 6) * 3;
    const size = 10 + (i % 4) * 6;
    const opacity = 0.06 + (i % 5) * 0.03;
    const color = colors[i % 3];
    const startY = -10 - (i % 5) * 10;
    css += `
.wp-el-${i} {
  position: fixed;
  left: ${left}%;
  top: ${startY}%;
  font-size: ${size}px;
  color: ${color};
  opacity: ${opacity};
  pointer-events: none;
  z-index: 0;
  animation: wpFloat${i % 4} ${dur}s linear ${delay}s infinite;
}
`;
  }

  css += `
@keyframes wpFloat0 { 0% { transform: translateY(0) rotate(0deg); } 100% { transform: translateY(120vh) rotate(360deg); } }
@keyframes wpFloat1 { 0% { transform: translateY(0) rotate(0deg) scale(1); } 50% { transform: translateY(60vh) rotate(180deg) scale(1.2); } 100% { transform: translateY(120vh) rotate(360deg) scale(1); } }
@keyframes wpFloat2 { 0% { transform: translateY(0) translateX(0); } 50% { transform: translateY(60vh) translateX(20px); } 100% { transform: translateY(120vh) translateX(0); } }
@keyframes wpFloat3 { 0% { transform: translateY(0) translateX(0) rotate(0deg); } 33% { transform: translateY(40vh) translateX(-15px) rotate(120deg); } 66% { transform: translateY(80vh) translateX(15px) rotate(240deg); } 100% { transform: translateY(120vh) translateX(0) rotate(360deg); } }
`;

  return css;
}

// Generate the floating element divs as a React-renderable array
export const getWallpaperElements = (wallpaperId) => {
  if (!wallpaperId || wallpaperId === 'none') return [];
  const wp = WALLPAPER_OPTIONS.find(w => w.id === wallpaperId);
  if (!wp) return [];
  
  const count = {
    hearts: 18, stars: 15, music: 12, bubbles: 20, diamonds: 10,
    clouds: 8, sparkle: 20, flowers: 14, waves: 10, confetti: 25
  }[wallpaperId] || 12;

  return Array.from({ length: count }, (_, i) => ({
    className: `wp-el-${i}`,
    char: wp.emoji
  }));
};
